import { Reveal } from "@/components/Reveal";
import { MonoLabel } from "@/components/primitives";
import { useSEO } from "@/lib/useSEO";
import { PageShell } from "@/components/PageShell";

const SECTIONS: { n: string; title: string; body: string }[] = [
  {
    n: "01",
    title: "The internet measures activity.",
    body: "Clicks, likes, streaks, commits, tasks closed. Every platform on earth has become a machine for counting presence — and presence has quietly become the only thing we reward.",
  },
  {
    n: "02",
    title: "Blockchains measure transactions.",
    body: "Settlement proves that something happened on-chain. It does not prove that anything changed because of it. A billion transactions can pass through a protocol without a single verifiable outcome behind them.",
  },
  {
    n: "03",
    title: "Social platforms measure attention.",
    body: "Reach is not results. Impressions are not impact. The most visible actor and the most effective actor are usually not the same actor — and our systems can no longer tell the difference.",
  },
  {
    n: "04",
    title: "None of them answer the real question.",
    body: "“What did the action actually change?” That is the only question that matters when capital, trust, and opportunity are being allocated — and no system on earth is built to answer it.",
  },
  {
    n: "05",
    title: "We propose a different primitive.",
    body: "Proof of Consequence. A verification layer that binds actors to measurable claims in advance, collects evidence of what actually happened, and settles the result — permanently — into a track record. The future economy will contain human workers, AI agents, autonomous businesses, protocols, robots, and software agents. They will all need one thing: a verifiable track record of outcomes.",
  },
];

export default function Manifesto() {
  useSEO({
    title: "Manifesto — A New Standard for Proof | Consequence Protocol",
    description:
      "Don’t prove that you worked. Prove that your work mattered. The Consequence Protocol manifesto.",
    path: "/manifesto",
  });

  return (
    <PageShell testId="page-manifesto">
      <section className="container-t max-w-3xl pb-32 pt-20 sm:pt-28">
        <Reveal>
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-cyan/50" />
            <MonoLabel>MANIFESTO</MonoLabel>
          </div>
          <h1 className="mt-5 font-display text-4xl font-bold uppercase leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            A new standard <span className="text-cyan glow-cyan">for proof</span>
          </h1>
        </Reveal>

        <div className="mt-16 space-y-14">
          {SECTIONS.map((s) => (
            <Reveal key={s.n} delay={0.05}>
              <article className="flex gap-6" data-testid={`manifesto-section-${s.n}`}>
                <span className="font-mono text-xs text-cyan/70">{s.n}</span>
                <div>
                  <h2 className="font-display text-xl font-bold leading-snug text-ink sm:text-2xl">{s.title}</h2>
                  <p className="mt-4 text-base leading-relaxed text-dim">{s.body}</p>
                </div>
              </article>
            </Reveal>
          ))}

          <Reveal delay={0.05}>
            <blockquote className="border-l-2 border-cyan pl-6" data-testid="manifesto-primitive">
              <MonoLabel className="text-cyan">The primitive</MonoLabel>
              <p className="mt-4 font-display text-2xl font-bold leading-snug text-ink sm:text-3xl">
                “The measure is not whether you acted. The measure is what happened because you acted.”
              </p>
            </blockquote>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-24 text-center" data-testid="manifesto-final">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-dim">Don’t prove that you worked.</p>
            <p className="mt-4 font-display text-3xl font-bold uppercase leading-tight tracking-tight text-cyan glow-cyan sm:text-4xl lg:text-5xl">
              Prove that your work mattered.
            </p>
          </div>
        </Reveal>
      </section>
    </PageShell>
  );
}
